/*
  Blog posts — edit this file to add/update posts.
  Slug is the URL path: /blog/{slug}.
  Content is a simple array of blocks: { type: "h2"|"p"|"quote"|"list"|"cta", text?, items?, href? }
*/

export const POSTS = [
  {
    slug: "how-ai-cut-dispatch-time-by-80-percent",
    title: "How AI Cut Our Dispatch Time by 80% — And Freed Up 20 Hours a Week",
    excerpt: "Small trucking companies spend 3-4 hours a day just texting drivers and customers. Here's how one 8-truck fleet in South Dakota flipped that to 45 minutes with AI-drafted messages.",
    cover_gradient: "from-[#FF5A1F]/40 via-[#141414] to-[#0A0A0A]",
    author: "Mike Gee",
    author_title: "Founder, FreightPilot AI",
    published_at: "2026-02-14",
    read_min: 6,
    tag: "Playbook",
    content: [
      { type: "p", text: "If you run a small trucking company, you already know the truth nobody talks about at the truck shows: the driver isn't the bottleneck. The paperwork isn't the bottleneck. The bottleneck is the messages." },
      { type: "p", text: "Every load kicks off a chain of communications — dispatch to driver, driver to shipper, shipper to receiver, back to accounting, back to the driver. Multiply that by 15 loads a week and you've just built yourself a full-time job on top of a full-time job." },
      { type: "h2", text: "The 3-hour morning ritual" },
      { type: "p", text: "Before we started using AI to draft dispatch messages, our mornings looked like this:" },
      { type: "list", items: [
        "6:30 AM — Coffee, check overnight delivery confirmations",
        "7:00 AM — Text each driver their load for the day (8 drivers, ~15 min of copy-paste)",
        "7:30 AM — Update 4 customer contacts about ETAs (each one wants a slightly different format)",
        "8:00 AM — Follow up on 3 overdue invoices with polite reminders",
        "9:00 AM — Answer 'where's my load?' calls from customers whose text I already sent",
        "9:30 AM — Finally start actually dispatching new loads",
      ] },
      { type: "p", text: "Three hours before we even started the real work. And this is a small fleet — 8 trucks. Imagine 30." },
      { type: "h2", text: "What we tried first (and why it failed)" },
      { type: "p", text: "We tried the obvious things. Templates in a Google Doc. A shared inbox. A cheap SMS blaster. Every one of them saved us maybe 20 minutes and cost us the personal touch — drivers started calling to ask why our messages sounded like robots wrote them." },
      { type: "p", text: "The problem with templates is that dispatch isn't templated. Every load has a driver personality, a customer preference, a route quirk. A message that works for James, an ex-Marine who wants bullet points, gets ignored by Marcus, who wants context and a joke to start the day." },
      { type: "quote", text: "We didn't need faster messages. We needed messages that sounded like WE sent them, but written in 3 seconds instead of 3 minutes." },
      { type: "h2", text: "Enter GPT-5.2 with dispatcher context" },
      { type: "p", text: "The unlock was giving the AI a small brain about our fleet — driver names, customer preferences, common lane profiles. Now we click one button per row and the AI drafts a message that mentions the specific truck number, references yesterday's on-time delivery, and signs off with our company name." },
      { type: "p", text: "It's not magic. It's context. And once you have it dialed in, one dispatcher becomes the throughput of three." },
      { type: "h2", text: "The math after 60 days" },
      { type: "list", items: [
        "Old ritual: 3 hours/day × 5 days = 15 hours/week on messaging",
        "New ritual: 45 min/day × 5 days = 3.75 hours/week",
        "Time recovered: 11.25 hours/week × 4 weeks = 45 hours/month",
        "Effective cost savings at $28/hr dispatcher wage: $1,260/month",
        "FreightPilot AI subscription cost: $299/month for 8 trucks",
        "Net monthly ROI: $961",
      ] },
      { type: "p", text: "But the number that mattered more than dollars was Sunday nights. My wife noticed first. I stopped bringing the laptop to the dinner table on Sundays. Because Monday morning wasn't a wall anymore — it was 45 minutes of AI drafts and a coffee." },
      { type: "cta", text: "Try the AI Playground →", href: "/#ai-playground" },
    ],
  },
  {
    slug: "a2p-10dlc-guide-for-trucking-companies",
    title: "A2P 10DLC: What Trucking Companies Need to Know Before Texting Drivers",
    excerpt: "If you're texting drivers dispatch info from a business number, you need A2P 10DLC registration. Skip it and you'll get throttled to 0 messages/day. Here's the full playbook.",
    cover_gradient: "from-[#007AFF]/40 via-[#141414] to-[#0A0A0A]",
    author: "Mike Gee",
    author_title: "Founder, FreightPilot AI",
    published_at: "2026-02-10",
    read_min: 8,
    tag: "Compliance",
    content: [
      { type: "p", text: "Since 2023, US carriers (T-Mobile, Verizon, AT&T) have required all businesses sending SMS to US mobile numbers to register through a compliance framework called A2P 10DLC (Application-to-Person 10-Digit Long Code)." },
      { type: "p", text: "If you don't register, you can still buy a phone number — but your messages will be silently throttled or blocked at the carrier level. And you won't know until your drivers start missing dispatch texts." },
      { type: "h2", text: "Who needs to register?" },
      { type: "p", text: "You need A2P 10DLC if all three are true:" },
      { type: "list", items: [
        "You send SMS to US mobile numbers",
        "You use a 10-digit US phone number (not a short code like 12345)",
        "The messages are business-to-person, not person-to-person",
      ] },
      { type: "p", text: "Texting your driver 'pickup confirmed for 8am' from a business number? That's A2P 10DLC territory." },
      { type: "h2", text: "The two-part registration" },
      { type: "p", text: "Registration has two stages: (1) Brand and (2) Campaign." },
      { type: "h2", text: "1. Brand registration ($4 one-time)" },
      { type: "p", text: "You verify your business exists. You need your Legal Business Name, EIN, business address, and website. If you're a sole proprietor with no EIN, there's a lower-throughput path using SSN — capped at 75 SMS/day." },
      { type: "h2", text: "2. Campaign registration ($10/month)" },
      { type: "p", text: "You describe what you'll be texting about. For trucking, the right use case is 'Account Notification' or 'Delivery Notification'." },
      { type: "p", text: "You'll submit 3 sample messages. Each MUST include an opt-out line like 'Reply STOP to opt out.' Carriers reject any campaign that doesn't." },
      { type: "quote", text: "The #1 reason A2P campaigns get rejected: skipping the STOP opt-out language in sample messages. Carriers auto-reject in seconds if it's missing." },
      { type: "h2", text: "What to say for opt-in method" },
      { type: "p", text: "For fleet dispatch use cases, the opt-in flow is: 'Drivers provide their phone number during hire onboarding, employer collects verbal or written consent for dispatch SMS.' That satisfies carrier requirements — you don't need every driver to click a link." },
      { type: "h2", text: "Timeline: 3-14 days" },
      { type: "list", items: [
        "Brand approval: usually within 24 hours (Twilio auto-approves clean submissions)",
        "Campaign approval: 1-7 business days depending on the carrier — T-Mobile is the slowest",
        "In-flight messages: SMS still works during pending, but throttled",
      ] },
      { type: "h2", text: "What happens if I skip it?" },
      { type: "p", text: "Carriers escalate: at first they'll throttle you. Then they'll silently filter your messages so drivers never see them. Eventually they'll blacklist your business phone number entirely." },
      { type: "p", text: "The correct path is: register the Brand first (starts the approval clock), buy your phone number in parallel, submit the Campaign with the number attached, then wait." },
      { type: "cta", text: "See how FreightPilot AI handles SMS →", href: "/services" },
    ],
  },
  {
    slug: "what-makes-a-load-profitable-fuel-driver-pay-and-the-hidden-math",
    title: "What Makes a Load Profitable? Fuel, Driver Pay, and the Hidden Math",
    excerpt: "$2.10/mile looks great on paper. Deadhead, fuel spread, driver percentage, and detention costs turn that same load into a $180 loss. Here's the calculator every dispatcher should run.",
    cover_gradient: "from-emerald-500/40 via-[#141414] to-[#0A0A0A]",
    author: "Mike Gee",
    author_title: "Founder, FreightPilot AI",
    published_at: "2026-02-06",
    read_min: 5,
    tag: "Ops",
    content: [
      { type: "p", text: "Every dispatcher gets the same call from a broker: '$2.10 per mile, 800 miles, ready today.' Sounds great. Feels great. Book it, drive it, invoice it." },
      { type: "p", text: "Then payroll runs, fuel receipts come in, and somehow that load lost you $180. What went wrong?" },
      { type: "h2", text: "The 5 hidden costs" },
      { type: "list", items: [
        "Deadhead miles to pickup (often 50-150 miles paid nothing)",
        "Deadhead miles back from delivery (or to next load)",
        "Fuel spread (diesel at destination vs. origin)",
        "Driver percentage (usually 25-30% of revenue)",
        "Detention risk (delays that eat driver hours-of-service)",
      ] },
      { type: "h2", text: "Let's run the actual math" },
      { type: "p", text: "800 miles at $2.10 = $1,680 revenue. Feels like $1,680 in your pocket. It isn't." },
      { type: "list", items: [
        "Deadhead 120 miles pickup + 80 miles from delivery = 200 miles unpaid",
        "Total mileage on truck: 1,000 miles",
        "Effective rate per truck mile: $1.68",
        "Fuel @ 6.5 mpg × $4.20/gal = $645",
        "Driver 27% of gross = $454",
        "Insurance/registration allocated per load ≈ $75",
        "Maintenance reserve ($0.15/mile) = $150",
        "Total costs: $1,324",
        "Net profit: $356 (before overhead like dispatcher time)",
      ] },
      { type: "p", text: "That's a real $356 — solid, not spectacular. But now imagine your driver hits 3 hours of detention at the receiver:" },
      { type: "list", items: [
        "Driver still gets paid: +$50",
        "Hours-of-service lost — next load must be shorter or pushed = $250 opportunity cost",
        "Net now: $56",
      ] },
      { type: "h2", text: "The rule of thumb" },
      { type: "p", text: "If deadhead is over 20% of paid miles, or if the fuel spread is over $0.15/gal against you, or if the load has known detention risk — bump your minimum rate to $2.40+/mile." },
      { type: "quote", text: "Bad loads don't feel bad in the moment. They only feel bad when payroll runs. That's why every load offer should get 30 seconds in a real profit calculator BEFORE you say yes." },
      { type: "cta", text: "Run the AI Profit Calculator →", href: "/signup" },
    ],
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}
